import { MessageTemplate, MessageTemplatePlaceholder } from '@/common/message-generate/message-generate.model';
import {
	ReservationMailBulkSendPayload,
	ReservationMailPreviewItem,
	ReservationOfferTransport,
	SMSBulkSendPayload,
	SMSMessage,
	SMSPreviewResponse,
} from '@/new-transport/_models/transport-list.model';
import { Result, SlotFlight } from '@/reservation/reservation.model';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({
	providedIn: 'root',
})
export class TransportListApiService {
	private readonly httpClient = inject(HttpClient);
	private readonly apiUrl = environment.apiUrl;

	getOfferTransport(transportIds: number[], params?: string): Observable<ReservationOfferTransport[]> {
		const ids = transportIds.join(',');
		return this.httpClient
			.get<
				Result<ReservationOfferTransport>
			>(`${this.apiUrl}/reservation/offer_transport/?transport=${ids}&${params}`)
			.pipe(map((result) => result?.results));
	}

	getOfferTransportBySegment(segmentId: number): Observable<ReservationOfferTransport[]> {
		return this.httpClient
			.get<Result<ReservationOfferTransport>>(`${this.apiUrl}/reservation/offer_transport/?segment=${segmentId}`)
			.pipe(map((result) => result?.results));
	}

	getSMSTemplateList(): Observable<MessageTemplate[]> {
		return this.httpClient
			.get<Result<MessageTemplate>>(`${this.apiUrl}/sms/template/`)
			.pipe(map((result) => result?.results));
	}

	getSMSTemplatePlaceholders(templateId: number): Observable<MessageTemplatePlaceholder[]> {
		return this.httpClient
			.get<Result<MessageTemplatePlaceholder>>(`${this.apiUrl}/sms/template_placeholders/?template=${templateId}`)
			.pipe(map((result) => result?.results));
	}

	getSMSMessage(ids: number[]): Observable<SMSMessage[]> {
		const params = new URLSearchParams();
		ids.forEach((id) => params.append('id', id.toString()));

		const queryString = params.toString();

		return this.httpClient
			.get<Result<SMSMessage>>(`${this.apiUrl}/sms/message/?${queryString}&limit=999`)
			.pipe(map((result) => result?.results));
	}

	createSMSPreview(payload: { slots: number[]; message: string }): Observable<SMSPreviewResponse[]> {
		return this.httpClient.post<SMSPreviewResponse[]>(
			`${this.apiUrl}/reservation/offer/slot/sms_preview/`,
			payload,
		);
	}

	createSMSBulkSend(payload: SMSBulkSendPayload[]): Observable<SMSMessage[]> {
		return this.httpClient.post<SMSMessage[]>(`${this.apiUrl}/sms/bulk_send/`, payload);
	}

	getMailTemplateList(): Observable<MessageTemplate[]> {
		return this.httpClient
			.get<Result<MessageTemplate>>(`${this.apiUrl}/mail/template/`)
			.pipe(map((result) => result?.results));
	}

	getMailTemplatePlaceholders(templateId: number): Observable<MessageTemplatePlaceholder[]> {
		return this.httpClient
			.get<
				Result<MessageTemplatePlaceholder>
			>(`${this.apiUrl}/mail/template_placeholders/?template=${templateId}`)
			.pipe(map((result) => result?.results));
	}

	createReservationMailPreview(payload: {
		reservations: number[];
		message: string;
		subject?: string;
		template?: number;
	}): Observable<ReservationMailPreviewItem[]> {
		return this.httpClient.post<ReservationMailPreviewItem[]>(`${this.apiUrl}/reservation/mail_preview/`, payload);
	}

	createReservationMailBulkSend(
		payload: ReservationMailBulkSendPayload[],
	): Observable<ReservationMailBulkSendPayload[]> {
		return this.httpClient.post<ReservationMailBulkSendPayload[]>(`${this.apiUrl}/mail/bulk_send/`, payload);
	}

	patchSlotFlight(slotId: number, payload: Partial<SlotFlight>): Observable<{ flight: SlotFlight }> {
		return this.httpClient.patch<{ flight: SlotFlight }>(`${this.apiUrl}/reservation/offer/slot/${slotId}/`, {
			flight: payload,
		});
	}
}
